import { resolveSelectedVersionIdAfterFetch } from "@/components/MedicalChronology/contexts/medicalChronologyVersionUtils";
import type {
  ChronologyVersion,
  ChronologyVersionResponse,
} from "@/components/MedicalChronology/types";
import type { MedChronService } from "@/services/medChronService";
import type { MedChronServiceV2 } from "@/services/medChronServiceV2";
import { formatDate } from "@/utils";
import { useCallback, useMemo, useState } from "react";
import { toast } from "react-hot-toast";

interface UseMedicalChronologyVersionsParams {
  caseId: string;
  medChronService: MedChronService;
  medChronServiceV2: MedChronServiceV2;
}

export const getVersionDisplayName = (version: ChronologyVersionResponse | null): string => {
  if (!version) return "";
  const formattedDate = formatDate(version.created_at);
  if (!formattedDate) return version.version_name;
  return `${version.version_name} - ${formattedDate}`;
};

export const useMedicalChronologyVersions = ({
  caseId,
  medChronService,
  medChronServiceV2,
}: UseMedicalChronologyVersionsParams) => {
  const [versions, setVersions] = useState<ChronologyVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [isVersionActionLoading, setIsVersionActionLoading] = useState(false);

  const currentVersion = useMemo(() => versions.find((v) => v.is_current) ?? null, [versions]);

  const fetchVersions = useCallback(async () => {
    try {
      const data = await medChronService.getChronologyVersions(caseId);
      const nextVersions = (data as ChronologyVersionResponse[]).map((version) => ({
        ...version,
        displayName: getVersionDisplayName(version),
      }));
      setVersions(nextVersions);
      setSelectedVersionId((previousId) =>
        resolveSelectedVersionIdAfterFetch(nextVersions, previousId)
      );
      return nextVersions;
    } catch (error) {
      console.error("Error fetching versions:", error);
      toast.error("Error loading versions");
      throw error;
    }
  }, [caseId, medChronService]);

  const fetchVersionsAndSelectCurrent = useCallback(async () => {
    const nextVersions = await fetchVersions();
    const nextCurrentVersion = nextVersions.find((v) => v.is_current);
    if (nextCurrentVersion) {
      setSelectedVersionId(nextCurrentVersion.version_id);
    }
  }, [fetchVersions]);

  const setSelectedVersionIdValidated = useCallback(
    (versionId: string) => {
      if (versions.some((v) => v.version_id === versionId)) {
        setSelectedVersionId(versionId);
      }
    },
    [versions]
  );

  const updateVersionName = useCallback(
    async (versionId: string, versionName: string) => {
      await medChronServiceV2.renameVersion(versionId, versionName);
      await fetchVersions();
    },
    [fetchVersions, medChronServiceV2]
  );

  const setVersionAsCurrent = useCallback(
    async (versionId: string) => {
      await medChronServiceV2.setCurrentVersion(versionId);
      await fetchVersions();
    },
    [fetchVersions, medChronServiceV2]
  );

  const duplicateVersion = useCallback(
    async (versionId: string) => {
      setIsVersionActionLoading(true);
      try {
        await medChronServiceV2.duplicateVersion(versionId);
        await fetchVersions();
      } finally {
        setIsVersionActionLoading(false);
      }
    },
    [fetchVersions, medChronServiceV2]
  );

  const deleteVersion = useCallback(
    async (versionId: string) => {
      setIsVersionActionLoading(true);
      try {
        await medChronServiceV2.deleteVersion(versionId);
        await fetchVersions();
      } finally {
        setIsVersionActionLoading(false);
      }
    },
    [fetchVersions, medChronServiceV2]
  );

  return {
    versions,
    selectedVersionId,
    currentVersion,
    isVersionActionLoading,
    fetchVersions,
    fetchVersionsAndSelectCurrent,
    setSelectedVersionId: setSelectedVersionIdValidated,
    updateVersionName,
    setVersionAsCurrent,
    duplicateVersion,
    deleteVersion,
  };
};
